import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  InputAdornment, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import StarIcon from "@mui/icons-material/Star";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { api, T, inputSx, fmtCAD } from "../ServicesManager/api";
import { API_URL } from "../../../config";

const buildImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/../${path}`;
};

/* ── Source type badge ── */
const SourceBadge: React.FC<{ type: string; vendorCount?: number }> = ({ type, vendorCount }) => {
  if (type === "inhouse") {
    return (
      <Chip icon={<HomeWorkOutlinedIcon sx={{ fontSize: 12 }} />} label="In-House" size="small"
        sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#edf7ee", color: "#2d6a35", "& .MuiChip-icon": { color: "#2d6a35" } }} />
    );
  }
  if (type === "vendor") {
    return (
      <Chip icon={<StorefrontOutlinedIcon sx={{ fontSize: 12 }} />}
        label={vendorCount && vendorCount > 0 ? `Vendor \u2022 ${vendorCount} Supplier${vendorCount !== 1 ? "s" : ""}` : "Vendor"}
        size="small"
        sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#fef9ec", color: "#92710c", "& .MuiChip-icon": { color: "#92710c" } }} />
    );
  }
  // both
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Chip icon={<HomeWorkOutlinedIcon sx={{ fontSize: 12 }} />} label="In-House" size="small"
        sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#edf7ee", color: "#2d6a35", "& .MuiChip-icon": { color: "#2d6a35" } }} />
      <Chip icon={<StorefrontOutlinedIcon sx={{ fontSize: 12 }} />}
        label={vendorCount && vendorCount > 0 ? `Vendor \u2022 ${vendorCount}` : "Vendor"}
        size="small"
        sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#fef9ec", color: "#92710c", "& .MuiChip-icon": { color: "#92710c" } }} />
    </Box>
  );
};

/* ── Pricing label ── */
const pricingLabel = (type: string | null | undefined) => {
  const map: Record<string, string> = {
    per_person: "Per Person", per_event: "Per Event", per_hour: "Per Hour",
    per_plate: "Per Plate", per_unit: "Per Unit", fixed: "Fixed",
  };
  return map[type ?? ""] || type || "";
};

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR EXPANSION ROW
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorExpansion: React.FC<{ vendorPrices: any[] }> = ({ vendorPrices }) => {
  if (!vendorPrices || vendorPrices.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, pl: 2, borderLeft: `2px solid #f5dfa8` }}>
      {vendorPrices.map((vp: any) => (
        <Box key={vp.id} sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 0.6, px: 1.5, borderRadius: "6px", bgcolor: "#fffdf6",
          border: "1px solid #f5eedc",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            {vp.is_default && <StarIcon sx={{ fontSize: 12, color: T.gold }} />}
            <Typography sx={{ fontSize: 12, fontWeight: vp.is_default ? 700 : 500, color: T.navy }}>
              {vp.vendor?.name ?? "Unknown Vendor"}
            </Typography>
            {vp.pricing_type && (
              <Chip label={pricingLabel(vp.pricing_type)} size="small"
                sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f0eef8", color: "#6b5eae" }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.greenTx }}>
            {fmtCAD(Number(vp.dcc_price ?? vp.vendor_price ?? 0))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM CARD
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemCard: React.FC<{ item: any }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const vendorPrices = item.vendor_prices ?? [];
  const hasVendors = vendorPrices.length > 0;
  const sourceType = item.source_type ?? "inhouse";
  const showExpand = (sourceType === "vendor" || sourceType === "both") && hasVendors;

  return (
    <Box sx={{
      p: 1.5, borderRadius: "10px", border: `1px solid ${T.line}`, bgcolor: T.white,
      transition: "all 0.15s", "&:hover": { borderColor: T.gold, bgcolor: "#fefcf8" },
      cursor: showExpand ? "pointer" : "default",
    }} onClick={() => showExpand && setExpanded(!expanded)}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: 0 }}>
          {item.image_url ? (
            <Box component="img" src={buildImageUrl(item.image_url) || ""} alt=""
              sx={{ width: 40, height: 40, borderRadius: "8px", objectFit: "cover", border: `1px solid ${T.line}`, flexShrink: 0 }} />
          ) : (
            <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: T.bg, border: `1px solid ${T.line}`,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ImageOutlinedIcon sx={{ fontSize: 16, color: T.slateLt }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }} noWrap>{item.name}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
              <SourceBadge type={sourceType} vendorCount={vendorPrices.length} />
              {item.unit && (
                <Chip label={pricingLabel(item.unit)} size="small"
                  sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f0eef8", color: "#6b5eae" }} />
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexShrink: 0 }}>
          {item.default_price != null && (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.greenTx }}>
              {fmtCAD(Number(item.default_price))}
            </Typography>
          )}
          {showExpand && (
            <IconButton size="small" sx={{ p: 0, color: T.slateLt }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Box>
      </Box>
      <Collapse in={expanded} timeout={200}>
        <VendorExpansion vendorPrices={vendorPrices} />
      </Collapse>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORY SECTION (collapsible)
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoryBlock: React.FC<{ sub: any }> = ({ sub }) => {
  const [open, setOpen] = useState(false);
  const items = sub.items ?? [];
  if (items.length === 0) return null;

  return (
    <Box sx={{ ml: 1 }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.8, py: 0.8, px: 1.5,
        borderRadius: "8px", cursor: "pointer",
        transition: "all 0.15s", "&:hover": { bgcolor: "#f3f0fa" },
      }} onClick={() => setOpen(!open)}>
        <IconButton size="small" sx={{ p: 0, color: "#8b5cf6" }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        <FolderOpenOutlinedIcon sx={{ fontSize: 15, color: "#8b5cf6" }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.navy }}>{sub.name}</Typography>
        <Chip label={items.length} size="small"
          sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
      </Box>
      <Collapse in={open} timeout={200}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1, pt: 0.5, pl: 3 }}>
          {items.map((item: any) => <ItemCard key={item.id} item={item} />)}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORY BLOCK (collapsible)
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoryBlock: React.FC<{ cat: any; serviceColor: string }> = ({ cat, serviceColor }) => {
  const [open, setOpen] = useState(false);
  const subs = cat.subcategories ?? [];
  const directItems = cat.items ?? [];
  const subItemCount = subs.reduce((t: number, s: any) => t + (s.items ?? []).length, 0);
  const totalItems = directItems.length + subItemCount;
  if (totalItems === 0 && subs.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.2, px: 2, cursor: "pointer",
        bgcolor: open ? "#f8f6f2" : T.white,
        transition: "background 0.15s", "&:hover": { bgcolor: "#f8f6f2" },
      }} onClick={() => setOpen(!open)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" sx={{ p: 0, color: "#3b82f6" }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: serviceColor, flexShrink: 0 }} />
          <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{cat.name}</Typography>
          {subs.length > 0 && (
            <Chip label={`${subs.length} sub`} size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#8b5cf612", color: "#8b5cf6" }} />
          )}
          <Chip label={`${totalItems} item${totalItems !== 1 ? "s" : ""}`} size="small"
            sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
        </Box>
      </Box>
      <Collapse in={open} timeout={250}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          {/* Subcategories */}
          {subs.map((sub: any) => <SubcategoryBlock key={sub.id} sub={sub} />)}

          {/* Direct items (in category, no subcategory) */}
          {directItems.length > 0 && (
            <Box sx={{ mt: subs.length > 0 ? 1 : 0 }}>
              {subs.length > 0 && (
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, pl: 1 }}>
                  Direct Items
                </Typography>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1 }}>
                {directItems.map((item: any) => <ItemCard key={item.id} item={item} />)}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SERVICE BLOCK (top-level collapsible)
   ═══════════════════════════════════════════════════════════════════════════ */
const ServiceBlock: React.FC<{ svc: any }> = ({ svc }) => {
  const [open, setOpen] = useState(true);
  const categories = svc.categories ?? [];

  const totalItems = useMemo(() => {
    let count = 0;
    categories.forEach((cat: any) => {
      count += (cat.items ?? []).length;
      (cat.subcategories ?? []).forEach((sub: any) => { count += (sub.items ?? []).length; });
    });
    return count;
  }, [categories]);

  return (
    <Box>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.5, px: 2, mb: 1, borderRadius: "12px",
        bgcolor: `${svc.color ?? T.slateLt}08`, border: `1px solid ${svc.color ?? T.slateLt}20`,
        cursor: "pointer", transition: "all 0.15s",
        "&:hover": { bgcolor: `${svc.color ?? T.slateLt}12` },
      }} onClick={() => setOpen(!open)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <IconButton size="small" sx={{ p: 0, color: svc.color ?? T.navy }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
          </IconButton>
          <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: svc.color ?? T.slateLt }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>{svc.name}</Typography>
          <Chip label={`${categories.length} categories`} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#3b82f610", color: "#3b82f6" }} />
          <Chip label={`${totalItems} items`} size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
        </Box>
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1.5, mb: 3 }}>
          {categories.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: T.slateLt, py: 2, pl: 2, fontStyle: "italic" }}>
              No categories in this service
            </Typography>
          ) : categories.map((cat: any) => (
            <CategoryBlock key={cat.id} cat={cat} serviceColor={svc.color ?? T.slateLt} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN SERVICE CATALOGUE PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const ServiceCataloguePage: React.FC = () => {
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const fetchCatalogue = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (sourceFilter !== "all") params.append("source_type", sourceFilter);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await api.get(`/banquet/service-catalogue${qs}`);
    if (res.code === 200) setServices(res.data ?? []);
    setLoading(false);
  }, [sourceFilter]);

  useEffect(() => { fetchCatalogue(); }, [fetchCatalogue]);

  // Client-side search filter
  const filteredServices = useMemo(() => {
    if (!search) return services;
    const s = search.toLowerCase();
    return services.map((svc: any) => ({
      ...svc,
      categories: (svc.categories ?? []).map((cat: any) => ({
        ...cat,
        items: (cat.items ?? []).filter((i: any) => i.name.toLowerCase().includes(s)),
        subcategories: (cat.subcategories ?? []).map((sub: any) => ({
          ...sub,
          items: (sub.items ?? []).filter((i: any) => i.name.toLowerCase().includes(s)),
        })).filter((sub: any) => (sub.items ?? []).length > 0),
      })).filter((cat: any) =>
        (cat.items ?? []).length > 0 ||
        (cat.subcategories ?? []).some((sub: any) => (sub.items ?? []).length > 0)
      ),
    })).filter((svc: any) => (svc.categories ?? []).length > 0);
  }, [services, search]);

  // Stats
  const stats = useMemo(() => {
    let cats = 0, subs = 0, items = 0, inhouseItems = 0, vendorItems = 0;
    services.forEach((svc: any) => {
      (svc.categories ?? []).forEach((cat: any) => {
        cats++;
        (cat.items ?? []).forEach((i: any) => {
          items++;
          if (i.source_type === "inhouse" || i.source_type === "both") inhouseItems++;
          if (i.source_type === "vendor" || i.source_type === "both") vendorItems++;
        });
        (cat.subcategories ?? []).forEach((sub: any) => {
          subs++;
          (sub.items ?? []).forEach((i: any) => {
            items++;
            if (i.source_type === "inhouse" || i.source_type === "both") inhouseItems++;
            if (i.source_type === "vendor" || i.source_type === "both") vendorItems++;
          });
        });
      });
    });
    return { services: services.length, categories: cats, items, inhouseItems, vendorItems };
  }, [services]);

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>
            Service Catalogue
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>
            All services (In-House and Vendor)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => window.location.href = "/banquet/services"}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 12, borderColor: T.line, color: T.slate, borderRadius: "10px", border: `1px solid ${T.line}`, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
            Edit In-House
          </Button>
          <Button size="small" onClick={() => window.location.href = "/vendors/services"}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 12, borderColor: T.line, color: T.slate, borderRadius: "10px", border: `1px solid ${T.line}`, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
            Edit Vendor
          </Button>
        </Box>
      </Box>

      {/* ── Stats ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 3 }}>
        {[
          { label: "Services", value: stats.services, icon: <AllInboxOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />, bg: `${T.gold}12` },
          { label: "Categories", value: stats.categories, icon: <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />, bg: "#3b82f612" },
          { label: "Total Items", value: stats.items, icon: <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.greenTx }} />, bg: T.greenBg },
          { label: "In-House", value: stats.inhouseItems, icon: <HomeWorkOutlinedIcon sx={{ fontSize: 18, color: "#2d6a35" }} />, bg: "#edf7ee" },
          { label: "Vendor", value: stats.vendorItems, icon: <StorefrontOutlinedIcon sx={{ fontSize: 18, color: "#92710c" }} />, bg: "#fef9ec" },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: stat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{stat.icon}</Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.navy, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Filters ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <ToggleButtonGroup
          value={sourceFilter} exclusive
          onChange={(_, v) => { if (v !== null) setSourceFilter(v); }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none", fontSize: 12, fontWeight: 600, px: 2, py: 0.6,
              borderColor: T.line, color: T.slate,
              "&.Mui-selected": { bgcolor: T.navy, color: T.white, borderColor: T.navy, "&:hover": { bgcolor: "#1a3550" } },
            },
          }}
        >
          <ToggleButton value="all">
            <AllInboxOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> All
          </ToggleButton>
          <ToggleButton value="inhouse">
            <HomeWorkOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> In-House
          </ToggleButton>
          <ToggleButton value="vendor">
            <StorefrontOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Vendor
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField size="small" placeholder="Search items..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 280 }} />

        {search && (
          <Button size="small" onClick={() => setSearch("")}
            sx={{ textTransform: "none", fontSize: 11, color: T.slateLt }}>Clear</Button>
        )}
      </Box>

      {/* ── Content ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: T.gold }} />
        </Box>
      ) : filteredServices.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "14px", border: `1px dashed ${T.line}` }}>
          <AllInboxOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt }}>
            {search ? "No items match your search." : "No services in the catalogue yet."}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {filteredServices.map((svc: any) => (
            <ServiceBlock key={svc.id} svc={svc} />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default ServiceCataloguePage;
